import {
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  Button,
} from '@chakra-ui/react';
import { Polls } from '@/model/polls';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';
import { useTranslation } from 'react-i18next';
import { PollsResultOptionRow } from './PollsResultOptionRow';

export interface PollsResultDetailModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  isLoading: boolean;
  item?: Polls;
  onClose: () => void;
}

const InfoField = ({ label, value }: { label: string; value: string }) => (
  <Flex direction='column' gap={1}>
    <Text fontSize='sm' color='gray.500'>
      {label}
    </Text>
    <Text>{value}</Text>
  </Flex>
);

export const PollsResultDetailModal = ({ isOpen, isLoading, item, onClose }: PollsResultDetailModalProps) => {
  const { t } = useTranslation();

  const options = [...(item?.poll_options ?? [])].sort((a, b) => b.votes - a.votes);
  const totalVotes = options.reduce((sum, option) => sum + option.votes, 0);
  const maxVotes = options.reduce((max, option) => Math.max(max, option.votes), 0);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent minW={{ md: '640px' }}>
          <ModalHeader>
            {t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_title')}
          </ModalHeader>
          <ModalCloseButton />

          {item ? (
            <ModalBody display='flex' flexDirection='column' gap={4}>
              <InfoField
                label={t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_identifier')}
                value={item.identifier}
              />
              <InfoField
                label={t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_question')}
                value={item.question}
              />

              <Flex gap={8}>
                <InfoField
                  label={t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_start_date')}
                  value={formatDateToDateTimeDisplay(item.created_at)}
                />
                <InfoField
                  label={t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_closing_date')}
                  value={item.closed_at ? formatDateToDateTimeDisplay(item.closed_at) : '-'}
                />
              </Flex>

              <InfoField
                label={t(
                  'pages.application_polls.components.polls_result_detail_modal.polls_result_detail_total_participants',
                )}
                value={String(item.total_participants)}
              />

              <Divider />

              <Flex direction='column' gap={2}>
                <Text fontWeight='bold'>
                  {t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_result')}
                </Text>

                {options.length === 0 ? (
                  <Text color='gray.500'>
                    {t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_empty')}
                  </Text>
                ) : (
                  <Flex direction='column' gap={3}>
                    {options.map((option, index) => (
                      <PollsResultOptionRow
                        key={option.uuid}
                        rank={index + 1}
                        option={option}
                        percentage={totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0}
                        maxPercentage={totalVotes > 0 ? (maxVotes / totalVotes) * 100 : 0}
                        isWinner={index === 0 && option.votes > 0}
                      />
                    ))}
                  </Flex>
                )}
              </Flex>
            </ModalBody>
          ) : (
            <ModalBody minH='200px' />
          )}

          <ModalFooter>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_polls.components.polls_result_detail_modal.polls_result_detail_close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={isLoading} />
    </>
  );
};
